<script setup>
import { Head, Link, router, useForm, usePage } from "@inertiajs/vue3";
import { ref, computed, watch } from "vue";
import DeleteIcons from "@/Components/Icons/DeleteIcons.vue";
import InputLabel from "@/Components/InputLabel.vue";
import InputError from "@/Components/InputError.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Header from "@/Components/Header.vue";
import Container from "@/Layouts/Container.vue";
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";
import Pagination from "@/Components/Pagination.vue";
import Swal from "sweetalert2";

const props = defineProps({
  sliders: {
    type: Object,
    required: true,
  },
});

const fileUrl = computed(() => `/storage/`);

// Computed property for row number calculation with fallbacks
const getRowNumber = computed(() => {
  return (index) => {
    const currentPage = props.sliders?.current_page || 1;
    const perPage = props.sliders?.per_page || 10;
    return (currentPage - 1) * perPage + index + 1;
  };
});

const form = useForm({
  id: null,
  judul: "",
  deskripsi: "",
  gambar: null,
  urutan: 0,
  status: true,
});

// Handle file upload
const handleFileUpload = (event) => {
  form.gambar = event.target.files[0] || "";
};

const fileInput = ref(null);

const submit = () => {
  if (form.id) {
    form.put(route("sliders.update", form.id), {
      onSuccess: () => {
        form.reset();
        Swal.fire({
          title: "Berhasil!",
          text: "Slider berhasil diperbarui",
          icon: "success",
          confirmButtonText: "OK",
        });
      },
      onError: () => {
        Swal.fire({
          title: "Error!",
          text: "Gagal memperbarui slider",
          icon: "error",
          confirmButtonText: "OK",
        });
      },
      preserveScroll: true,
    });
  } else {
    form.post(route("sliders.store"), {
      onSuccess: () => {
        form.reset("judul", "deskripsi", "urutan", "status");
        if (fileInput.value) {
          fileInput.value.value = null;
        }
        Swal.fire({
          title: "Berhasil!",
          text: "Slider berhasil ditambahkan",
          icon: "success",
          confirmButtonText: "OK",
        });
      },
      onError: () => {
        Swal.fire({
          title: "Error!",
          text: "Gagal menambahkan slider",
          icon: "error",
          confirmButtonText: "OK",
        });
      },
      preserveScroll: true,
      forceFormData: true,
    });
  }
};

const deleteSlider = (sliderId) => {
  Swal.fire({
    title: "Apakah Anda yakin?",
    text: "Data slider akan dihapus permanen!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, hapus!",
    cancelButtonText: "Batal",
  }).then((result) => {
    if (result.isConfirmed) {
      form.delete(route("sliders.destroy", sliderId), {
        onSuccess: () => {
          Swal.fire({
            title: "Terhapus!",
            text: "Slider berhasil dihapus",
            icon: "success",
            confirmButtonText: "OK",
          });
        },
        onError: () => {
          Swal.fire({
            title: "Error!",
            text: "Gagal menghapus slider",
            icon: "error",
            confirmButtonText: "OK",
          });
        },
        preserveScroll: true,
      });
    }
  });
};
const editSlider = (slider) => {
  form.id = slider.id;
  form.judul = slider.judul;
  form.deskripsi = slider.deskripsi;
  form.urutan = slider.urutan;
  form.status = slider.status;
  // Kita tidak mengisi form.gambar karena itu adalah file input
};

const cancelEdit = () => {
  form.reset("judul", "deskripsi", "urutan", "status");
  form.clearErrors();
  form.id = null;
};

let search = ref(usePage().props.search || ""),
  pageNumber = ref(1);

let sliderUrl = computed(() => {
  let url = new URL(route("sliders.index"));
  url.searchParams.append("page", pageNumber.value);
  if (search.value) {
    url.searchParams.append("search", search.value);
  }
  return url;
});

const updatedPageNumber = (link) => {
  if (link && link.url) {
    const url = new URL(link.url);
    pageNumber.value = url.searchParams.get("page") || 1;
  }
};

watch(
  () => sliderUrl.value,
  (updateSliderUrl) => {
    router.visit(updateSliderUrl, {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    });
  }
);

watch(
  () => search.value,
  (value) => {
    if (value) {
      pageNumber.value = 1;
    }
  }
);
</script>

<template>
  <Head title="Slider" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Slider', href: route('sliders.index') },
          ]"
        />

        <div class="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <div
            class="order-2 col-span-1 space-y-6 lg:order-1"
            v-if="$page.props.auth.user && $page.props.can.slider_create"
          >
            <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
              <div
                class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl sm:px-6 sm:py-4"
              >
                <h2
                  class="flex items-center text-lg font-bold text-white sm:text-xl"
                >
                  <i class="mr-2 fa-solid fa-images sm:mr-3"></i>
                  {{ form.id ? "Edit Slider" : "Tambah Slider" }}
                </h2>
              </div>
              <div class="p-4 sm:p-6">
                <form class="space-y-4" @submit.prevent="submit">
                  <!-- Judul -->
                  <div>
                    <label
                      for="judul"
                      class="block mb-2 text-sm font-medium text-gray-900"
                    >
                      Judul Slider
                    </label>
                    <input
                      id="judul"
                      type="text"
                      v-model="form.judul"
                      class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
                      placeholder="Masukkan judul slider"
                      required
                    />
                    <InputError :message="form.errors.judul" class="mt-2" />
                  </div>

                  <!-- Deskripsi -->
                  <div>
                    <label
                      for="deskripsi"
                      class="block mb-2 text-sm font-medium text-gray-900"
                    >
                      Deskripsi
                    </label>
                    <textarea
                      id="deskripsi"
                      v-model="form.deskripsi"
                      rows="3"
                      class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
                      placeholder="Masukkan deskripsi slider"
                    ></textarea>
                    <InputError :message="form.errors.deskripsi" class="mt-2" />
                  </div>

                  <!-- Gambar -->
                  <div>
                    <label
                      for="gambar"
                      class="block mb-2 text-sm font-medium text-gray-900"
                    >
                      Gambar Slider
                    </label>
                    <input
                      id="gambar"
                      type="file"
                      ref="fileInput"
                      @change="handleFileUpload"
                      accept="image/*"
                      class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                    />
                    <progress
                      v-if="form.progress"
                      :value="form.progress.percentage"
                      max="100"
                      class="w-full h-2 mt-2 bg-gray-200 rounded-lg"
                    >
                      {{ form.progress.percentage }}%
                    </progress>
                    <InputError :message="form.errors.gambar" class="mt-2" />
                  </div>

                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <!-- Urutan -->
                    <div>
                      <label
                        for="urutan"
                        class="block mb-2 text-sm font-medium text-gray-900"
                      >
                        Urutan
                      </label>
                      <input
                        id="urutan"
                        type="number"
                        v-model="form.urutan"
                        class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
                        placeholder="1"
                        min="0"
                        required
                      />
                      <InputError :message="form.errors.urutan" class="mt-2" />
                    </div>

                    <!-- Status -->
                    <div>
                      <label
                        class="block mb-2 text-sm font-medium text-gray-900"
                      >
                        Status
                      </label>
                      <div class="flex items-center">
                        <input
                          id="status"
                          v-model="form.status"
                          type="checkbox"
                          class="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2"
                        />
                        <label
                          for="status"
                          class="ml-2 text-sm font-medium text-gray-900"
                        >
                          Aktif
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- Submit Buttons -->
                  <div class="pt-4 space-y-2">
                    <PrimaryBtn
                      :disabled="form.processing"
                      class="w-full px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      <i class="mr-2 fa-solid fa-floppy-disk"></i>
                      {{
                        form.processing
                          ? "Menyimpan..."
                          : form.id
                          ? "Update Slider"
                          : "Simpan Slider"
                      }}
                    </PrimaryBtn>
                    <button
                      v-if="form.id"
                      type="button"
                      @click="cancelEdit"
                      class="w-full px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                    >
                      <i class="mr-2 fa-solid fa-times"></i>
                      Batal
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div class="order-1 col-span-1 lg:col-span-2 lg:order-2">
            <!-- Header Section -->
            <div class="mb-4 sm:mb-6">
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">
                    Daftar Slider
                  </h3>
                  <p class="mt-1 text-sm text-gray-600">
                    Kelola slider gambar untuk beranda website
                  </p>
                </div>
                <div class="mt-2 text-sm text-gray-500 sm:mt-0">
                  Total: {{ sliders.total }} slider
                </div>
              </div>
            </div>

            <!-- Search Input -->
            <div class="relative mb-4">
              <div
                class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
              >
                <MagnifyingGlassVue class="w-4 h-4 text-gray-400" />
              </div>
              <input
                v-model="search"
                type="text"
                autocomplete="off"
                placeholder="Search slider..."
                id="search"
                class="block w-full px-3 py-2.5 pl-10 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
              />
            </div>

            <!-- Table -->
            <div
              class="overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
            >
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-300">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        #
                      </th>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 min-w-[150px]"
                      >
                        Slider
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden md:table-cell min-w-[200px]"
                      >
                        Gambar
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden sm:table-cell"
                      >
                        Info
                      </th>
                      <th
                        scope="col"
                        class="relative py-3.5 pl-3 pr-4 sm:pr-6 min-w-[100px]"
                      >
                        <span class="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr
                      v-for="(slider, index) in sliders.data"
                      :key="slider.id"
                      class="transition-colors duration-150 hover:bg-gray-50"
                    >
                      <td
                        class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6"
                      >
                        {{ getRowNumber(index) }}
                      </td>
                      <td class="px-3 py-4 text-sm">
                        <div class="flex items-center">
                          <div class="flex-shrink-0 w-12 h-12 mr-3">
                            <img
                              v-if="slider.gambar"
                              :src="slider.gambar"
                              :alt="slider.judul"
                              class="object-cover w-12 h-12 border border-gray-200 rounded-lg shadow-sm"
                            />
                            <div
                              v-else
                              class="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-lg"
                            >
                              <i class="text-gray-400 fa-solid fa-image"></i>
                            </div>
                          </div>
                          <div class="flex-grow min-w-0">
                            <span
                              class="block font-medium text-gray-900 truncate"
                            >
                              {{ slider.judul }}
                            </span>
                            <div class="text-xs text-gray-500 truncate">
                              {{ slider.deskripsi || "Tidak ada deskripsi" }}
                            </div>
                            <!-- Mobile view: Show status -->
                            <div class="mt-1 sm:hidden">
                              <span
                                :class="
                                  slider.status
                                    ? 'inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full'
                                    : 'inline-flex items-center px-2 py-1 text-xs font-medium text-red-800 bg-red-100 rounded-full'
                                "
                              >
                                {{ slider.status ? "Aktif" : "Tidak Aktif" }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td
                        class="hidden px-3 py-4 text-sm text-gray-500 md:table-cell"
                      >
                        <div class="flex items-center justify-center">
                          <img
                            v-if="slider.gambar"
                            :src="slider.gambar"
                            :alt="slider.judul"
                            class="object-cover w-20 h-12 transition-shadow duration-200 border border-gray-200 rounded-lg shadow-sm hover:shadow-md"
                          />
                          <div
                            v-else
                            class="flex items-center justify-center w-20 h-12 bg-gray-100 rounded-lg"
                          >
                            <i class="text-gray-400 fa-solid fa-image"></i>
                          </div>
                        </div>
                      </td>
                      <td
                        class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell"
                      >
                        <div class="space-y-1">
                          <div class="text-xs">
                            <span class="font-medium">Urutan:</span>
                            {{ slider.urutan }}
                          </div>
                          <div>
                            <span
                              :class="
                                slider.status
                                  ? 'inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full'
                                  : 'inline-flex items-center px-2 py-1 text-xs font-medium text-red-800 bg-red-100 rounded-full'
                              "
                            >
                              {{ slider.status ? "Aktif" : "Tidak Aktif" }}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td
                        class="relative py-4 pl-3 pr-4 text-sm font-medium text-right sm:pr-6"
                      >
                        <div
                          class="flex items-center justify-end space-x-1 sm:space-x-2"
                        >
                          <!-- Action Buttons -->
                          <Link
                            v-if="$page.props.can.slider_show"
                            :href="route('sliders.show', slider.idslider)"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 hover:text-blue-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="View"
                          >
                            <i class="w-3 h-3 fa-solid fa-eye"></i>
                            <span class="hidden ml-1 sm:inline">View</span>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Pagination -->
            <Pagination
              :data="sliders"
              :updatedPageNumber="updatedPageNumber"
            />
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>
